import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { STEP } from './creationSteps/utils';
import { useNotify, useRedirect } from 'react-admin';
import WizardStepper from '../../core/components/wizardStepper/WizardStepper';
import { CreationFundContext } from './creationSteps/CreationFundContext';
import FormTitleBar from '../../core/components/FormTitleBar';
import { getInitials } from '../../core/utils';
import FundDetailsStep from './creationSteps/FundDetailsStep';
import { isEmpty } from 'lodash';
import AddRelatedInvestors from './creationSteps/AddRelatedInvestors';
import AddRelatedContacts from './creationSteps/AddRelatedContacts/AddRelatedContacts';
import UserApi from '../../core/apis/UserApi';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';
import { GenerateMessageOnLeave } from '../../core/components/wizardStepper/GenerateMessageOnLeave';

const FundCreate = props => {
  const [currentAuthorizations, setCurrentAuthorizations] = useState([]);
  const contacts = useRef([]);
  const [contactAuthorizationsIds, setContactAuthorizationsIds] = useState([]);
  const { permissions = [] } = props;
  const [activeStep, setActiveStep] = useState(0);
  const [fundContext, setFundContext] = useState({});
  const redirect = useRedirect();
  const providerFormContext = useMemo(() => ({ fundContext, setFundContext }), [
    fundContext,
    setFundContext
  ]);
  const [isNextAvailable, setIsNextAvailable] = useState(false);
  const [isSaveAndCloseStep, setIsSaveAndCloseStep] = useState(false);
  const shouldQuitRef = useRef(false);
  const [isSpinnerOnNextButton, setIsSpinnerOnNextButton] = useState(false);
  const [shouldHideBackButton, setShouldHideBackButton] = useState(false);
  const [isFirstStepPristine, setIsFirstStepPristine] = useState(true);
  const authorizationsGridToDeleteRef = useRef([]);
  const [permissionGroupsUploaded, setPermissionGroupsUploaded] = useState([]);
  const [listOfSteps, setListOfSteps] = useState([
    {
      name: STEP.fundDetails,
      label: 'Fund Details',
      leavingPromptLabel: 'Fund Details',
      description:
        'Please fill in the mandatory fields to create new fund. All other fund details you can add later.',
      component: FundDetailsStep,
      completed: false
    }
  ]);
  const notify = useNotify();
  const fundCreatedRef = useRef({});
  const notifyServerError = useNotifyServerError();

  const [
    hasEditFundPermission,
    hasAddFundInvestorRelationShip,
    hasAssignUserAuthorization,
    hasDeleteFundInvestorRelationship
  ] = useMemo(
    () => [
      permissions.includes('AUTH_EDIT_FUND'),
      permissions.includes('AUTH_WRITE_FUND_INVESTOR'),
      permissions.includes('AUTH_WRITE_USER_AUTHORIZATION'),
      permissions.includes('AUTH_DELETE_FUND_INVESTOR')
    ],
    [permissions]
  );

  useEffect(() => {
    if (hasAddFundInvestorRelationShip) {
      setListOfSteps(prev => [
        ...prev,
        {
          name: STEP.addRelatedInvestors,
          label: 'Add Related Investors',
          leavingPromptLabel: 'Related Investors',
          description:
            'Select one or multiple Investors from the list to add them to the Fund.',
          component: AddRelatedInvestors,
          completed: false
        }
      ]);
    }

    if (hasAddFundInvestorRelationShip && hasAssignUserAuthorization) {
      setListOfSteps(prev => [
        ...prev,
        {
          name: STEP.addRelatedContacts,
          label: 'Add Related Contacts',
          leavingPromptLabel: 'Related Contacts',
          description:
            'Add one or more contacts to the fund from a list below.',
          component: AddRelatedContacts,
          completed: false
        }
      ]);
    }
  }, [hasAddFundInvestorRelationShip, hasAssignUserAuthorization]);

  const isLastStep = useCallback(() => {
    return !!(!isEmpty(listOfSteps) && listOfSteps.length === activeStep + 1);
  }, [activeStep, listOfSteps]);

  const isItSaveAndCloseStep = useCallback(() => {
    if (!isEmpty(listOfSteps)) {
      return !!(
        listOfSteps[activeStep].name === STEP.addRelatedInvestors &&
        !isLastStep()
      );
    }
  }, [listOfSteps, activeStep, isLastStep]);

  useEffect(() => {
    if (listOfSteps[activeStep].name === STEP.fundDetails) {
      const pristine = fundContext?.detailsFormState?.pristine;
      const valid = fundContext?.detailsFormState?.valid;
      const errors = fundContext?.detailsFormState?.errors;
      const values = fundContext?.detailsFormState?.values;

      const hasDataRoomError = Boolean(errors?.dataRoom);
      const coInvNeedsMainFund =
        values?.investmentStructure === 'Co-Investment' && !values?.mainFundId;

      const nextEnabled =
        !pristine && valid && !hasDataRoomError && !coInvNeedsMainFund;

      setIsNextAvailable(nextEnabled);
    }

    if (
      listOfSteps[activeStep].name === STEP.addRelatedInvestors &&
      !hasEditFundPermission
    ) {
      setShouldHideBackButton(true);
    } else {
      setShouldHideBackButton(false);
    }

    if (listOfSteps[activeStep].name === STEP.addRelatedContacts) {
      setIsNextAvailable(true);
    }

    setIsSaveAndCloseStep(isItSaveAndCloseStep());
  }, [
    activeStep,
    fundContext?.detailsFormState,
    hasEditFundPermission,
    listOfSteps,
    isItSaveAndCloseStep
  ]);

  const getActiveStep = () => {
    const step = listOfSteps[activeStep];
    return (
      <step.component
        variant="standard"
        onSubmitEnd={onSubmitEnd}
        setFundContext={setFundContext}
        fundContext={fundContext}
        shouldQuitRef={shouldQuitRef}
        setIsSpinnerOnNextButton={setIsSpinnerOnNextButton}
        setIsNextAvailable={setIsNextAvailable}
        setIsFirstStepPristine={setIsFirstStepPristine}
        fundCreatedRef={fundCreatedRef}
        authorizationsGridToDeleteRef={authorizationsGridToDeleteRef}
        permissionGroupsUploaded={permissionGroupsUploaded}
        setCurrentAuthorizations={setCurrentAuthorizations}
        currentAuthorizations={currentAuthorizations}
        hasAssignUserAuthorization={hasAssignUserAuthorization}
        contacts={contacts}
        contactAuthorizationsIds={contactAuthorizationsIds}
        setContactAuthorizationsIds={setContactAuthorizationsIds}
        hasDeleteFundInvestorRelationship={hasDeleteFundInvestorRelationship}
        setListOfSteps={setListOfSteps}
        listOfSteps={listOfSteps}
        {...props}
      />
    );
  };

  const handleClose = useCallback(() => {
    if (isEmpty(fundCreatedRef.current)) {
      redirect('list', '/funds');
    } else {
      notify('ra.notification.created');
      redirect(`/funds/${fundCreatedRef.current.id}`);
    }
  }, [redirect, notify, fundCreatedRef]);

  const onSubmitEnd = useCallback(() => {
    if (activeStep === listOfSteps.length - 1 || shouldQuitRef.current) {
      handleClose();
    }
    if (
      listOfSteps[activeStep].name === STEP.fundDetails ||
      listOfSteps[activeStep].name === STEP.addRelatedInvestors
    ) {
      setActiveStep(prev => prev + 1);
    }
  }, [activeStep, handleClose, listOfSteps]);

  const handleNextForm = e => {
    const buttonClicked = e.currentTarget.getAttribute('buttonkey');
    if (buttonClicked === 'saveAndClose') {
      shouldQuitRef.current = true;
    }

    if (listOfSteps[activeStep].name === STEP.fundDetails) {
      document
        .getElementById('formFundDetails')
        .dispatchEvent(new Event('submit', { cancelable: true }));
    }
    if (listOfSteps[activeStep].name === STEP.addRelatedInvestors) {
      document
        .getElementById('formAddRelatedInvestors')
        .dispatchEvent(new Event('submit', { cancelable: true }));
    }

    if (listOfSteps[activeStep].name === STEP.addRelatedContacts) {
      UserApi.createUserauthorizations(currentAuthorizations)
        .then(res => {
          setPermissionGroupsUploaded(res);
          const updatedListOfStep = listOfSteps.map(step => {
            if (step.name === STEP.addRelatedContacts) {
              return { ...step, completed: true };
            }
            return step;
          });
          setListOfSteps(updatedListOfStep);
          onSubmitEnd();
        })
        .catch(e => notifyServerError(e));
    }
  };

  const handleBackForm = () => {
    if (
      listOfSteps[activeStep].name === STEP.addRelatedInvestors &&
      hasEditFundPermission
    ) {
      setActiveStep(prev => prev - 1);
    }

    if (listOfSteps[activeStep].name === STEP.addRelatedContacts) {
      setActiveStep(prev => prev - 1);
    }
  };

  const displayCreatedInfo = () => {
    return (
      <FormTitleBar
        recordLabel={'Fund ID: ' + fundContext.fund.id}
        name={fundContext.fund.name}
        status={fundContext.fund.status}
        statusLabel={'Status:'}
        showAvatar={true}
        avatarContent={getInitials(fundContext.fund.name)}
      />
    );
  };

  return (
    <WizardStepper
      handleClose={handleClose}
      getActiveStep={getActiveStep}
      handleNextForm={handleNextForm}
      resource="Fund"
      activeListOfSteps={listOfSteps}
      activeStep={activeStep}
      providerFormContext={providerFormContext}
      customContext={CreationFundContext}
      generateMessageOnLeave={
        <GenerateMessageOnLeave
          listOfSteps={listOfSteps}
          resource={'Fund'}
          resourceName={fundContext?.fund?.name}
        />
      }
      resourceCreated={fundContext.fund}
      isNextAvailable={isNextAvailable}
      isSpinnerOnNextButton={isSpinnerOnNextButton}
      shouldDisplayResourceCreatedInfo={true}
      displayResourceCreatedInfo={displayCreatedInfo}
      handleBackForm={handleBackForm}
      shouldHideBackButton={shouldHideBackButton}
      isFirstStepPristine={isFirstStepPristine}
      isSaveAndCloseStep={isSaveAndCloseStep}
    />
  );
};

export default FundCreate;
